import { useState } from 'react'
import { Upload } from 'lucide-react'
import { FileRow } from '@klear/ui/components/document-upload-page'
import { FileDropZone } from '@klear/ui/components/file-drop-zone'
import { failedUploadsMessage, useSendForReview } from '@klear/ui/hooks/use-document-upload-request'
import { UPLOAD_ACCEPTED_LABEL, UPLOAD_MANUAL_ENTRY_NOTE, UPLOAD_ACCEPT_LIST } from '@klear/upload/upload-formats'
import { Alert, AlertDescription } from '@klear/ui/components/alert'
import { Button } from '@klear/ui/components/button'
import { Label } from '@klear/ui/components/label'
import { toast } from '@klear/ui/hooks/use-toast'

type OrderDocumentUploadProps = {
  traderId: number
  busy: boolean
  onBusyChange: (busy: boolean) => void
  onDone: () => void
}

export function OrderDocumentUpload({ traderId, busy, onBusyChange, onDone }: OrderDocumentUploadProps) {
  const { send, statuses, reset } = useSendForReview()
  const [files, setFiles] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)

  const choose = (chosen: File[]) => {
    reset()
    setError(null)
    setFiles(chosen)
  }

  const submit = async () => {
    if (files.length === 0) return
    setError(null)
    setFiles([])
    onBusyChange(true)
    const failed = await send(files, traderId, null).finally(() => onBusyChange(false))
    if (failed.length > 0) {
      setError(failedUploadsMessage(failed, files.length))
      setFiles(failed.map((row) => row.file))
      return
    }
    toast({
      title: 'Sent to Klear for review',
      description: 'Klear reads the files and files them on the right orders. They show here with their status.'
    })
    onDone()
  }

  return (
    <div className="space-y-4">
      {error !== null && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      <div className="grid gap-1.5">
        <Label htmlFor="order-document-files">Files *</Label>
        <FileDropZone
          id="order-document-files"
          files={files}
          onFilesChange={choose}
          accept={UPLOAD_ACCEPT_LIST}
          acceptedLabel={UPLOAD_ACCEPTED_LABEL}
          acceptedNote={UPLOAD_MANUAL_ENTRY_NOTE}
          disabled={busy}
        />
      </div>
      {statuses.length > 0 && (
        <ul className="rounded-md border px-3">
          {statuses.map((row, index) => (
            <FileRow key={`${row.file.name}-${index}`} row={row} />
          ))}
        </ul>
      )}
      <div className="flex justify-end space-x-2">
        <Button type="button" variant="outline" onClick={onDone} disabled={busy}>
          Cancel
        </Button>
        <Button type="button" onClick={() => void submit()} disabled={files.length === 0 || busy}>
          <Upload className="mr-2 h-4 w-4" />
          {busy ? 'Uploading...' : 'Upload Document'}
        </Button>
      </div>
    </div>
  )
}
